import { Navigate, Outlet, useParams } from 'react-router-dom';
import { useAccountAuth } from '../auth';
import { useDistrict } from '../district';
import { useUserSetupStatus } from './useUserSetupStatus';

/**
 * ユーザー設定(オンボーディング)必須の本体ルート (map / post / list / map-download / analysis、
 * および :sessionId 配下) を保護する layout guard。
 *
 * 未設定の一般ユーザー (未参加 or 復帰コード未取得) が本体ルートへ直接アクセスした場合に、
 * 入口 `/m/:urlToken` (EntryPage) へ強制送還する。EntryPage 側で「新規参加」または
 * 「復帰コード入力/発行」に振り分けられ、ユーザー設定を完了させてから本体に入れる。
 *
 * 例外:
 *   - 関係者 (master / 担当地区管理者) はロールで閲覧可のため、メンバーシップ無しでも通す
 *     (docs/112 §3-2「単に中身を確認したいだけの場合はメンバーシップ未作成のまま閲覧可」)。
 *   - 地区の「強い停止」は上位 DistrictGate (App.tsx) が先に NonPublicScreen を出すため、本 guard
 *     配下には未停止 (or 関係者) のみ到達する。
 */
export function RequireOnboarding() {
  const { urlToken } = useParams();
  const account = useAccountAuth();
  const districtId = useDistrict()?.id ?? null;
  const uid = account.state.status === 'authenticated' ? account.state.user.uid : null;
  const status = useUserSetupStatus(uid, districtId);

  // 認証 / 地区解決 / 設定判定の待ち (この間は判断を保留してローダー表示)。
  if (account.state.status === 'loading' || status === 'loading') {
    return <div className="p-8 text-center text-slate-500">読み込み中…</div>;
  }
  // 関係者はオンボーディング強制の対象外 (ロールで閲覧可)。
  if (account.state.role === 'master' || account.state.role === 'district') {
    return <Outlet />;
  }
  // 一般ユーザーで未設定 (未参加 or 復帰コード未取得) → 入口へ強制送還。
  if (status !== 'complete') {
    return <Navigate to={`/m/${urlToken}`} replace />;
  }
  return <Outlet />;
}
